import { useMutation, useSuspenseQuery } from '@apollo/client';
import React, { useMemo, useState } from 'react';

import {
  Box,
  ColDefBuilder,
  DataTable,
  Heading,
  NewButton,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Text,
  VStack,
  useToast,
} from '@eluve/components';
import { useUserIdFromSession } from '@eluve/session-helpers';

import {
  getEluveAdminUsersQuery,
  getExperimentByIdQuery,
  updateTestingOverridesMutation,
} from './operations';

type TestingOverride = {
  userId: string;
  variantKey: string;
};

type Variant = {
  id: string;
  name: string;
  posthogVariantKey: string;
};

interface TestingOverridesSectionProps {
  experimentId: string;
  variants: Variant[];
  testingOverrides: TestingOverride[];
}

type OverrideRow = {
  userId: string;
  email: string;
  firstName: string;
  lastName: string;
  variantKey: string;
  variantName: string;
};

export const TestingOverridesSection: React.FC<
  TestingOverridesSectionProps
> = ({ experimentId, variants, testingOverrides }) => {
  const toast = useToast();
  const currentUserId = useUserIdFromSession();

  const { data: usersData } = useSuspenseQuery(getEluveAdminUsersQuery);
  const eluveAdminUsers = useMemo(
    () => usersData?.users ?? [],
    [usersData?.users],
  );

  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [selectedVariantKey, setSelectedVariantKey] = useState<string>('');

  const [updateTestingOverrides] = useMutation(updateTestingOverridesMutation, {
    refetchQueries: [
      { query: getExperimentByIdQuery, variables: { experimentId } },
    ],
    onError: () => {
      toast.error('Failed to update testing overrides');
    },
  });

  const sortedUsers = useMemo(() => {
    return [...eluveAdminUsers].sort((a, b) => {
      if (a.id === currentUserId) return -1;
      if (b.id === currentUserId) return 1;
      return a.email.localeCompare(b.email);
    });
  }, [eluveAdminUsers, currentUserId]);

  const availableUsers = useMemo(() => {
    const overrideUserIds = new Set(testingOverrides.map((o) => o.userId));
    return sortedUsers.filter((u) => !overrideUserIds.has(u.id));
  }, [sortedUsers, testingOverrides]);

  const rows: OverrideRow[] = useMemo(() => {
    return testingOverrides.map((override) => {
      const user = eluveAdminUsers.find((u) => u.id === override.userId);
      const variant = variants.find(
        (v) => v.posthogVariantKey === override.variantKey,
      );
      return {
        userId: override.userId,
        email: user?.email ?? 'Unknown',
        firstName: user?.firstName ?? '',
        lastName: user?.lastName ?? '',
        variantKey: override.variantKey,
        variantName: variant?.name ?? override.variantKey,
      };
    });
  }, [testingOverrides, eluveAdminUsers, variants]);

  const handleAddOverride = async () => {
    if (!selectedUserId || !selectedVariantKey) return;

    const newOverrides: TestingOverride[] = [
      ...testingOverrides,
      { userId: selectedUserId, variantKey: selectedVariantKey },
    ];

    await updateTestingOverrides({
      variables: {
        experimentId,
        testingOverrides: newOverrides,
      },
    });

    setSelectedUserId('');
    setSelectedVariantKey('');
  };

  const handleRemoveOverride = async (userId: string) => {
    const newOverrides = testingOverrides.filter((o) => o.userId !== userId);

    await updateTestingOverrides({
      variables: {
        experimentId,
        testingOverrides: newOverrides,
      },
    });
  };

  const cols = new ColDefBuilder<OverrideRow>()
    .defaultSortable('email', 'Email')
    .colDef({
      header: 'Name',
      accessorFn: (row) => `${row.firstName} ${row.lastName}`.trim(),
    })
    .defaultSortable('variantName', 'Variant')
    .colDef({
      header: 'Actions',
      cell: ({ row }) => (
        <NewButton
          type="outline"
          size="s"
          icon={{ name: 'Trash' }}
          onClick={() => handleRemoveOverride(row.original.userId)}
        />
      ),
    })
    .build();

  return (
    <VStack gap={4}>
      <VStack gap={1}>
        <Heading size="lg">Testing Overrides</Heading>
        <Text size="sm" color="tertiary">
          Add users to bypass PostHog and always receive a specific variant.
          Useful for testing experiments before rollout.
        </Text>
      </VStack>

      <div className="grid w-full grid-cols-1 gap-4 md:grid-cols-3">
        <Select value={selectedUserId} onValueChange={setSelectedUserId}>
          <SelectTrigger>
            <SelectValue placeholder="Select user" />
          </SelectTrigger>
          <SelectContent>
            {availableUsers.map((user) => (
              <SelectItem key={user.id} value={user.id}>
                {user.email}
                {user.id === currentUserId && ' (you)'}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={selectedVariantKey}
          onValueChange={setSelectedVariantKey}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select variant" />
          </SelectTrigger>
          <SelectContent>
            {variants.map((variant) => (
              <SelectItem key={variant.id} value={variant.posthogVariantKey}>
                {variant.name} ({variant.posthogVariantKey})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <NewButton
          text="Add Override"
          onClick={handleAddOverride}
          disabled={!selectedUserId || !selectedVariantKey}
        />
      </div>

      {rows.length === 0 ? (
        <Box className="rounded-lg border border-dashed border-border-primary p-6 text-center">
          <Text color="tertiary">
            No testing overrides configured. Add users above to test specific
            variants.
          </Text>
        </Box>
      ) : (
        <DataTable data={rows} columns={cols} isPaginated={false} />
      )}
    </VStack>
  );
};
